import { Link, useNavigate } from 'react-router-dom';
import { Check, Sparkles, Phone, Mail, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Mic, Eye, Languages, TrendingUp, Brain } from 'lucide-react';

const PricingPage = () => {
  const navigate = useNavigate();

  const basicPlanFeatures = [
    'AI-assisted session notes',
    'Smart scheduling & calendar sync',
    'WhatsApp-first workflow',
    'Patient management dashboard',
    'Direct payment coordination',
    'Consent & documentation tools'
  ];

  const premiumFeatures = [
    { icon: TrendingUp, text: 'Longitudinal progress tracking' },
    { icon: Mic, text: 'Voice tone pattern analysis' },
    { icon: Eye, text: 'Expression & emotional trend analysis' },
    { icon: Languages, text: 'Multimodal Indian language support' },
    { icon: Brain, text: 'Cultural & context insights' }
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Navigation */}
      <nav className="border-b border-stone-100 bg-white/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-18 py-4">
            <Link to="/" className="flex items-center gap-2.5" data-testid="logo">
              <div className="w-9 h-9 rounded-xl bg-[#2F5233] flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <span 
                className="font-semibold text-xl text-stone-900 tracking-tight"
                style={{ fontFamily: 'Fraunces, Georgia, serif' }}
              >
                ClarityStack
              </span>
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <Link to="/#features" className="text-sm font-medium text-stone-600 hover:text-[#2F5233] transition-colors">
                Features
              </Link>
              <Link to="/pricing" className="text-sm font-medium text-[#2F5233] transition-colors">
                Pricing
              </Link>
              <Link to="/contact" className="text-sm font-medium text-stone-600 hover:text-[#2F5233] transition-colors">
                Contact
              </Link>
            </div>
            
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/login')}
                className="text-stone-600 hover:text-stone-900 font-medium"
              >
                Login
              </Button>
              <Button 
                className="bg-[#2F5233] hover:bg-[#243f28] text-white rounded-full px-6 font-medium shadow-sm"
                onClick={() => navigate('/register')}
              >
                Start Free Trial
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="py-16 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-sm font-medium text-[#2F5233] mb-3">Simple Pricing</p>
            <h1 
              className="text-4xl font-semibold text-stone-900 mb-4"
              style={{ fontFamily: 'Fraunces, Georgia, serif' }}
            >
              Start Your Free 14-Day Trial
            </h1>
            <p className="text-stone-600">
              No credit card required. Full access to all features. Cancel anytime.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-16">
            {/* Basic Plan */}
            <div className="bg-white rounded-2xl border-2 border-[#2F5233] p-8 shadow-lg shadow-[#2F5233]/10" data-testid="pricing-card-basic">
              <div className="mb-6">
                <h2 
                  className="text-xl font-semibold text-stone-900 mb-2"
                  style={{ fontFamily: 'Fraunces, Georgia, serif' }}
                >
                  Basic Plan
                </h2>
                <p className="text-sm text-stone-500 mb-4">Your AI-Powered Practice Assistant</p>
                <div className="flex items-baseline gap-1 mb-1">
                  <span 
                    className="text-4xl font-semibold text-stone-900"
                    style={{ fontFamily: 'Fraunces, Georgia, serif' }}
                  >
                    ₹1,499
                  </span>
                  <span className="text-stone-500">/month</span>
                </div>
                <p className="text-xs text-stone-400">Inclusive of GST</p>
              </div>
              
              <ul className="space-y-3 mb-8">
                {basicPlanFeatures.map((feature) => (
                  <li key={`basic-${feature.slice(0, 20)}`} className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-[#2F5233] flex-shrink-0 mt-0.5" />
                    <span className="text-stone-600">{feature}</span>
                  </li>
                ))}
              </ul>
              
              <Button 
                size="lg"
                className="w-full bg-[#2F5233] hover:bg-[#243f28] text-white rounded-full font-medium"
                onClick={() => navigate('/register')}
              >
                <span className="mr-2">▶</span>
                Start Free Trial
              </Button>
              <p className="text-xs text-stone-400 text-center mt-3">
                No credit card required
              </p>
            </div>

            {/* Premium Plan - Coming Soon */}
            <div className="bg-stone-50 rounded-2xl border border-dashed border-stone-300 p-8 relative" data-testid="pricing-card-premium">
              <Badge className="absolute -top-3 left-6 bg-stone-200 text-stone-600 border-0 font-medium">
                Coming Soon
              </Badge>
              <div className="mb-6">
                <h2 
                  className="text-xl font-semibold text-stone-900 mb-2"
                  style={{ fontFamily: 'Fraunces, Georgia, serif' }}
                >
                  Premium Plan
                </h2>
                <p className="text-sm text-stone-400 mb-4">Advanced insights and analytics</p>
                <p className="text-sm text-stone-400">Pricing to be announced</p>
              </div>
              
              <p className="text-sm text-stone-500 mb-4">Everything in Basic, plus:</p>
              <ul className="space-y-3 mb-8">
                {premiumFeatures.map((feature) => (
                  <li key={`premium-${feature.text.slice(0, 20)}`} className="flex items-start gap-3 text-stone-400">
                    <feature.icon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                    <span>{feature.text}</span>
                  </li>
                ))}
              </ul>
              
              <Button 
                size="lg"
                variant="outline"
                className="w-full rounded-full font-medium border-stone-300 text-stone-500 hover:bg-white hover:text-stone-700"
                onClick={() => toast.success('Thanks for your interest! We will notify you when Premium launches.')}
              >
                Join Waitlist
              </Button>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="border-t border-stone-200 pt-16">
            <h2 
              className="text-2xl font-semibold text-stone-900 mb-8 text-center"
              style={{ fontFamily: 'Fraunces, Georgia, serif' }}
            >
              Frequently Asked Questions
            </h2>
            
            <div className="space-y-6 max-w-2xl mx-auto">
              <div className="p-5 bg-stone-50 rounded-xl">
                <h3 className="font-medium text-stone-900 mb-2">What&apos;s included in the 14-day free trial?</h3>
                <p className="text-stone-600 text-sm leading-relaxed">
                  Full access to all Basic Plan features. No credit card required to start.
                </p>
              </div>
              
              <div className="p-5 bg-stone-50 rounded-xl">
                <h3 className="font-medium text-stone-900 mb-2">Can I cancel anytime?</h3>
                <p className="text-stone-600 text-sm leading-relaxed">
                  Yes. You can cancel before your next billing cycle to avoid future charges.
                </p>
              </div>
              
              <div className="p-5 bg-stone-50 rounded-xl">
                <h3 className="font-medium text-stone-900 mb-2">Are patient payments processed through ClarityStack?</h3>
                <p className="text-stone-600 text-sm leading-relaxed">
                  No. Patient payments are made directly between you and your patients via UPI. ClarityStack only helps coordinate and track payments.
                </p>
              </div>
              
              <div className="p-5 bg-stone-50 rounded-xl">
                <h3 className="font-medium text-stone-900 mb-2">Is my data secure?</h3>
                <p className="text-stone-600 text-sm leading-relaxed">
                  Yes. We implement industry-standard security practices and encryption. See our Privacy Policy for details.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-16 px-6 lg:px-8 bg-stone-900 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            {/* Company Info */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-9 h-9 rounded-xl bg-[#2F5233] flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <span 
                  className="font-semibold text-xl tracking-tight"
                  style={{ fontFamily: 'Fraunces, Georgia, serif' }}
                >
                  ClarityStack
                </span>
              </div>
              <p className="text-stone-400 text-sm mb-6">
                ClarityStack is a product of Think Through Enterprise
              </p>
              <div className="text-sm text-stone-400 space-y-2">
                <p className="flex items-start gap-3">
                  <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0 text-stone-500" />
                  <span>B104 Shantideep Society, Sathya Sai Complex,<br />Chikuwadi, Borivali West, Mumbai – 400092</span>
                </p>
                <p className="flex items-center gap-3">
                  <Phone className="h-4 w-4 flex-shrink-0 text-stone-500" />
                  <span>+91 9967124157</span>
                </p>
                <p className="flex items-center gap-3">
                  <Mail className="h-4 w-4 flex-shrink-0 text-stone-500" />
                  <span>support@claritystack.live</span>
                </p>
              </div>
            </div>
            
            {/* Links */}
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-3 text-sm">
                <li><Link to="/#features" className="text-stone-400 hover:text-white transition-colors">Features</Link></li>
                <li><Link to="/pricing" className="text-stone-400 hover:text-white transition-colors">Pricing</Link></li>
                <li><Link to="/contact" className="text-stone-400 hover:text-white transition-colors">Contact</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-3 text-sm">
                <li><Link to="/privacy" className="text-stone-400 hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-stone-400 hover:text-white transition-colors">Terms & Conditions</Link></li>
                <li><Link to="/refund" className="text-stone-400 hover:text-white transition-colors">Refund Policy</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-stone-800">
            <p className="text-sm text-stone-500 text-center">
              © {new Date().getFullYear()} Think Through Enterprise. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
